/*
 * Copyright (c) 2026 GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';

import { AppProviders } from '@/app/providers';
import { App } from '@/app/App';
import { initializeMsal } from '@/auth/msalConfig';
import { setUnauthorizedHandler } from '@/lib/apiClient';
import { router } from '@/routes/router';
import '@/i18n/i18n';
import '@/styles/index.css';
import '@/styles/motion.css';

const container = document.getElementById('root');
if (!container) {
  throw new Error('Root container #root was not found in the document.');
}

// A 401 from the API means the session is missing/expired or lacks access. Route to the
// Unauthorized page (once) instead of letting the app silently degrade to empty/partial data.
setUnauthorizedHandler(() => {
  if (router.state.location.pathname !== '/unauthorized') {
    void router.navigate('/unauthorized');
  }
});

// After an MSAL redirect, some IdP configurations (e.g. a redirect URI registered as "Web"
// instead of "Single-page application") leave the auth response params (?code, ?state, ...) in
// the address bar, so the URL looks "stuck". handleRedirectPromise (awaited in initializeMsal)
// has already consumed whatever it can, so strip the leftovers to restore a clean URL.
function stripAuthResponseParams(): void {
  const url = new URL(window.location.href);
  const junk = ['code', 'state', 'session_state', 'client_info', 'error', 'error_description'];
  let changed = false;
  for (const key of junk) {
    if (url.searchParams.has(key)) {
      url.searchParams.delete(key);
      changed = true;
    }
  }
  if (changed) {
    window.history.replaceState(
      window.history.state,
      '',
      `${url.pathname}${url.search}${url.hash}`,
    );
  }
}

// MSAL must init before render (handles redirect response).
void initializeMsal().finally(() => {
  stripAuthResponseParams();
  createRoot(container).render(
    <StrictMode>
      <AppProviders>
        <App />
      </AppProviders>
    </StrictMode>,
  );
});
